"use client";

import { useTranslations } from "next-intl";
import { Eye } from "lucide-react";
import { Container } from "@/components/ui/container";
import { useAuth } from "@/components/providers/auth-provider";

/**
 * Sticky full-width banner when an admin is viewing the site as a student (impersonation JWT).
 */
export function ImpersonationBanner() {
  const t = useTranslations("common");
  const { isImpersonating, user, endImpersonation, isLoading } = useAuth();

  if (isLoading || !isImpersonating || !user?.impersonation) return null;

  const label = user.impersonation.subjectName?.trim() || user.name;

  return (
    <div
      className="sticky top-0 z-[200] border-b border-amber-300/60 bg-gradient-to-r from-amber-50 via-amber-100/90 to-amber-50 text-amber-950 shadow-sm backdrop-blur-sm"
      role="status"
      aria-live="polite"
    >
      <Container className="flex flex-col gap-2 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="flex min-w-0 items-start gap-2 sm:items-center">
          <Eye className="mt-0.5 h-4 w-4 shrink-0 text-amber-800 sm:mt-0" aria-hidden />
          <p className="min-w-0 text-sm leading-snug">
            <span className="font-semibold">{t("impersonation.bannerTitle")}</span>{" "}
            <span className="font-medium text-amber-950">{label}</span>
            <span className="text-amber-900/80">: {t("impersonation.bannerHint")}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={() => void endImpersonation()}
          className="inline-flex shrink-0 items-center justify-center rounded-lg bg-amber-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-amber-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-900"
        >
          {t("impersonation.endButton")}
        </button>
      </Container>
    </div>
  );
}
